import React, { useState, useEffect, useRef } from 'react';
import axios from '../../../axiosConfig';
import styles from '../MicroProbe/Companies/CustomDropdownFinancialData.module.css';

const ForeignExchangeDropdown = ({ onSelect, label }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [fromQuery, setFromQuery] = useState('');
  const [toQuery, setToQuery] = useState('');
  const [fromSuggestions, setFromSuggestions] = useState([]);
  const [toSuggestions, setToSuggestions] = useState([]);
  const [fromLoading, setFromLoading] = useState(false);
  const [toLoading, setToLoading] = useState(false);
  const [selectedFromCurrency, setSelectedFromCurrency] = useState('');
  const [selectedToCurrency, setSelectedToCurrency] = useState('');
  const dropdownRef = useRef(null);

  const loadSuggestions = async (query, setSuggestions, setLoading) => {
    if (query.length > 1) {
      setLoading(true);
      try {
        const response = await axios.get(`/api/fxsearch/?q=${query}`);
        setSuggestions(response.data);
      } catch (error) {
        console.error('Error fetching FX suggestions:', error);
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    } else {
      setSuggestions([]);
    }
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => loadSuggestions(fromQuery, setFromSuggestions, setFromLoading), 300);
    return () => clearTimeout(timeoutId);
  }, [fromQuery]);

  useEffect(() => {
    const timeoutId = setTimeout(() => loadSuggestions(toQuery, setToSuggestions, setToLoading), 300);
    return () => clearTimeout(timeoutId);
  }, [toQuery]);

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  const handleFromCurrencySelect = (fx) => {
    setSelectedFromCurrency(fx.code);
    setFromQuery(fx.name);
    setFromSuggestions([]);
    checkAndSubmitPair(fx.code, selectedToCurrency);
  };

  const handleToCurrencySelect = (fx) => {
    setSelectedToCurrency(fx.code);
    setToQuery(fx.name);
    setToSuggestions([]);
    checkAndSubmitPair(selectedFromCurrency, fx.code);
  };

  const checkAndSubmitPair = (fromCode, toCode) => {
    if (fromCode && toCode) {
      const fxData = {
        name: `${fromCode}/${toCode}`,
        code: `${fromCode}/${toCode}`,
        type: 'fx'
      };
      onSelect(fxData.code, fxData);
      // Reset selections
      setSelectedFromCurrency('');
      setSelectedToCurrency('');
      setFromQuery('');
      setToQuery('');
      setFromSuggestions([]);
      setToSuggestions([]);
      setIsOpen(false);
    }
    // Don't close dropdown if only one currency is selected
  };

  const handleClickOutside = (event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setIsOpen(false);
      // Clear suggestions when clicking outside
      setFromSuggestions([]);
      setToSuggestions([]);
      // Reset any partial selections when clicking outside
      setSelectedFromCurrency('');
      setSelectedToCurrency('');
      setFromQuery('');
      setToQuery('');
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className={styles.customDropdown} ref={dropdownRef}>
      <div 
        className={styles.selected} 
        onClick={toggleDropdown}
        style={{ backgroundColor: '#cce7f0' }}
      >
        <span>{label}</span>
        <span className={`${styles.arrow} ${isOpen ? styles.open : ''}`} />
      </div>
      {isOpen && (
        <div className={styles.dropdownMenu} style={{ maxHeight: '400px', overflowY: 'auto' }}>
          <div style={{ padding: '10px', borderBottom: '1px solid #eee' }}>
            {/* From Currency Section */}
            <div style={{ marginBottom: '15px' }}>
              <label style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: '5px', display: 'block' }}>
                From Currency:
              </label>
              <input
                type="text"
                placeholder="Search from currency..."
                value={fromQuery}
                onChange={(e) => setFromQuery(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              />
              {fromLoading && (
                <div style={{ padding: '5px', fontSize: '12px', color: '#666' }}>
                  Loading...
                </div>
              )}
              {!fromLoading && fromSuggestions.length > 0 && (
                <div style={{ maxHeight: '100px', overflowY: 'auto', border: '1px solid #ddd', borderTop: 'none' }}>
                  {fromSuggestions.slice(0, 5).map((suggestion, index) => (
                    <div
                      key={suggestion.id || index}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleFromCurrencySelect(suggestion);
                      }}
                      style={{ 
                        padding: '5px 8px',
                        cursor: 'pointer',
                        borderBottom: '1px solid #f0f0f0',
                        fontSize: '13px',
                        backgroundColor: '#fff'
                      }}
                      onMouseEnter={(e) => e.target.style.backgroundColor = '#f0f0f0'}
                      onMouseLeave={(e) => e.target.style.backgroundColor = '#fff'}
                    >
                      <div style={{ fontWeight: 'bold' }}>{suggestion.name}</div>
                      <div style={{ fontSize: '11px', color: '#666' }}>
                        {suggestion.code} - {suggestion.country}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* To Currency Section */}
            <div style={{ marginBottom: '10px' }}>
              <label style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: '5px', display: 'block' }}>
                To Currency:
              </label>
              <input
                type="text"
                placeholder="Search to currency..."
                value={toQuery}
                onChange={(e) => setToQuery(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              />
              {toLoading && (
                <div style={{ padding: '5px', fontSize: '12px', color: '#666' }}>
                  Loading...
                </div>
              )}
              {!toLoading && toSuggestions.length > 0 && (
                <div style={{ maxHeight: '100px', overflowY: 'auto', border: '1px solid #ddd', borderTop: 'none' }}>
                  {toSuggestions.slice(0, 5).map((suggestion, index) => (
                    <div
                      key={suggestion.id || index}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleToCurrencySelect(suggestion);
                      }}
                      style={{ 
                        padding: '5px 8px',
                        cursor: 'pointer',
                        borderBottom: '1px solid #f0f0f0',
                        fontSize: '13px',
                        backgroundColor: '#fff'
                      }}
                      onMouseEnter={(e) => e.target.style.backgroundColor = '#f0f0f0'}
                      onMouseLeave={(e) => e.target.style.backgroundColor = '#fff'}
                    >
                      <div style={{ fontWeight: 'bold' }}>{suggestion.name}</div>
                      <div style={{ fontSize: '11px', color: '#666' }}>
                        {suggestion.code} - {suggestion.country}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Selected Pair Display */}
            {selectedFromCurrency && selectedToCurrency && (
              <div style={{ 
                padding: '8px',
                backgroundColor: '#e8f5e8',
                border: '1px solid #28a745',
                borderRadius: '4px',
                fontSize: '13px',
                textAlign: 'center'
              }}>
                Selected: {selectedFromCurrency}/{selectedToCurrency}
              </div>
            )}
            
            {/* Instruction */}
            <div style={{ 
              fontSize: '12px', 
              color: '#666', 
              textAlign: 'center',
              marginTop: '10px',
              fontStyle: 'italic'
            }}>
              Select both currencies to add the pair
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ForeignExchangeDropdown;
